import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  Plus, MagnifyingGlass, Upload, Download, Trash, FileText, FilePdf, FileDoc, CloudArrowUp,
  Folder, Buildings, Users, Tag, ShieldCheck, ArrowsClockwise, Eye,
} from '@phosphor-icons/react';
import {
  getDocuments, uploadDocument, downloadDocument, viewDocument, deleteDocument,
  getDossiers, getOrganizations, getContacts,
} from '../services/api';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHero } from '../components/motion/PageHero';

const typeDocumentOptions = [
  { value: 'devis_ess', label: 'Devis ESS' },
  { value: 'proposition_adhesion', label: "Proposition d'adhésion" },
  { value: 'proposition_contribution', label: 'Proposition de contribution' },
  { value: 'contrat', label: 'Contrat' },
  { value: 'convention', label: 'Convention' },
  { value: 'attestation', label: 'Attestation' },
  { value: 'recu_ess', label: 'Reçu ESS' },
  { value: 'justificatif', label: 'Justificatif' },
  { value: 'kbis', label: 'KBIS / Extrait' },
  { value: 'piece_identite', label: "Pièce d'identité" },
  { value: 'rib', label: 'RIB' },
  { value: 'bilan', label: 'Bilan comptable' },
  { value: 'statuts', label: 'Statuts' },
  { value: 'note_interne', label: 'Note interne' },
  { value: 'courrier', label: 'Courrier' },
  { value: 'fiche_contact', label: 'Fiche contact' },
  { value: 'autre', label: 'Autre' },
];

const statutOptions = [
  { value: 'brouillon', label: 'Brouillon', color: 'bg-gray-100 text-gray-700' },
  { value: 'recu', label: 'Reçu', color: 'bg-blue-100 text-blue-800' },
  { value: 'en_attente', label: 'En attente', color: 'bg-amber-100 text-amber-800' },
  { value: 'valide', label: 'Validé', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'rejete', label: 'Rejeté', color: 'bg-red-100 text-red-800' },
  { value: 'archive', label: 'Archivé', color: 'bg-gray-100 text-gray-500' },
];

const getFileIcon = (contentType) => {
  if (contentType?.includes('pdf')) return <FilePdf size={20} className="text-red-500" weight="fill" />;
  if (contentType?.includes('word') || contentType?.includes('document')) return <FileDoc size={20} className="text-blue-500" weight="fill" />;
  return <FileText size={20} className="text-[#5E6D66]" />;
};

const formatFileSize = (bytes) => {
  if (!bytes) return '-';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const labelOf = (list, value) => list.find((o) => o.value === value)?.label || value || '-';

function StatutBadge({ statut }) {
  const opt = statutOptions.find((s) => s.value === statut);
  return (
    <span
      data-testid={`doc-statut-${statut || 'none'}`}
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium ${opt?.color || 'bg-gray-100 text-gray-700'}`}
    >
      {opt?.label || statut || '—'}
    </span>
  );
}

export function DocumentsPage() {
  const fileInputRef = useRef(null);
  const [documents, setDocuments] = useState([]);
  const [dossiers, setDossiers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatut, setFilterStatut] = useState('all');
  const [filterDossier, setFilterDossier] = useState('all');
  const [filterOrg, setFilterOrg] = useState('all');
  const [filterContact, setFilterContact] = useState('all');

  const [tab, setTab] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploadData, setUploadData] = useState({
    titre: '',
    type_document: 'autre',
    dossier_id: 'none',
    organization_id: 'none',
    contact_id: 'none',
    file: null,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterType !== 'all') params.type_document = filterType;
      if (filterStatut !== 'all') params.statut = filterStatut;
      if (filterDossier !== 'all') params.dossier_id = filterDossier;
      if (filterOrg !== 'all') params.organization_id = filterOrg;
      if (filterContact !== 'all') params.contact_id = filterContact;
      params.limit = 500;

      const [docsRes, dossiersRes, orgsRes, contactsRes] = await Promise.all([
        getDocuments(params),
        getDossiers(),
        getOrganizations(),
        getContacts({ limit: 500 }),
      ]);
      setDocuments(docsRes.data.items || []);
      setDossiers(dossiersRes.data.items || []);
      setOrganizations(orgsRes.data.items || []);
      setContacts(contactsRes.data.items || []);
    } catch (e) {
      toast.error('Erreur lors du chargement de la GED ESS');
    } finally {
      setLoading(false);
    }
  }, [filterType, filterStatut, filterDossier, filterOrg, filterContact]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetUploadForm = () => {
    setUploadData({
      titre: '', type_document: 'autre',
      dossier_id: 'none', organization_id: 'none', contact_id: 'none',
      file: null,
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadData.file) { toast.error('Veuillez sélectionner un fichier'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', uploadData.file);
      fd.append('titre', uploadData.titre || uploadData.file.name);
      fd.append('type_document', uploadData.type_document);
      if (uploadData.dossier_id && uploadData.dossier_id !== 'none') fd.append('dossier_id', uploadData.dossier_id);
      if (uploadData.organization_id && uploadData.organization_id !== 'none') fd.append('organization_id', uploadData.organization_id);
      if (uploadData.contact_id && uploadData.contact_id !== 'none') fd.append('contact_id', uploadData.contact_id);

      await uploadDocument(fd);
      toast.success('Document téléversé');
      setDialogOpen(false);
      resetUploadForm();
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors du téléversement');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await downloadDocument(doc.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename || doc.titre || 'document');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleView = async (doc) => {
    try {
      const res = await viewDocument(doc.id);
      const contentType = doc.content_type || res.headers?.['content-type'] || 'application/octet-stream';
      const url = window.URL.createObjectURL(new Blob([res.data], { type: contentType }));
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) {
        // Popup bloque : forcer download
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch {
      toast.error('Erreur lors de la visualisation');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      await deleteDocument(id);
      toast.success('Document supprimé');
      fetchData();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const dossiersById = useMemo(() => Object.fromEntries(dossiers.map((d) => [d.id, d])), [dossiers]);
  const orgsById = useMemo(() => Object.fromEntries(organizations.map((o) => [o.id, o])), [organizations]);
  const contactsById = useMemo(() => Object.fromEntries(contacts.map((c) => [c.id, c])), [contacts]);

  const getDossierNum = useCallback((id) => dossiersById[id]?.numero_dossier || '-', [dossiersById]);
  const getOrgName = useCallback((id) => orgsById[id]?.raison_sociale || '-', [orgsById]);
  const getContactName = useCallback((id) => {
    const c = contactsById[id];
    if (!c) return '-';
    return `${c.prenom || ''} ${c.nom || ''}`.trim() || c.email || '-';
  }, [contactsById]);

  // Apply textual search client-side (titre / filename)
  const filteredDocuments = useMemo(() => {
    if (!search) return documents;
    const q = search.toLowerCase();
    return documents.filter((d) =>
      d.titre?.toLowerCase().includes(q) ||
      d.original_filename?.toLowerCase().includes(q)
    );
  }, [documents, search]);

  // Group helpers for classification tabs
  const groupBy = useCallback((keyFn, labelFn) => {
    const map = new Map();
    for (const d of filteredDocuments) {
      const k = keyFn(d) || '__none__';
      if (!map.has(k)) map.set(k, { label: labelFn(k, d), key: k, items: [] });
      map.get(k).items.push(d);
    }
    return Array.from(map.values()).sort((a, b) => b.items.length - a.items.length);
  }, [filteredDocuments]);

  const groupedByDossier = useMemo(() =>
    groupBy(
      (d) => d.dossier_id,
      (k) => (k === '__none__' ? 'Sans dossier' : getDossierNum(k))
    ), [groupBy, getDossierNum]);

  const groupedByOrg = useMemo(() =>
    groupBy(
      (d) => d.organization_id,
      (k) => (k === '__none__' ? 'Sans organisation' : getOrgName(k))
    ), [groupBy, getOrgName]);

  const groupedByContact = useMemo(() =>
    groupBy(
      (d) => d.contact_id,
      (k) => (k === '__none__' ? 'Sans contact' : getContactName(k))
    ), [groupBy, getContactName]);

  const groupedByType = useMemo(() =>
    groupBy(
      (d) => d.type_document,
      (k) => (k === '__none__' ? 'Non typé' : labelOf(typeDocumentOptions, k))
    ), [groupBy]);

  // KPIs
  const totalCount = documents.length;
  const totalSize = documents.reduce((acc, d) => acc + (d.size || 0), 0);
  const typesCount = new Set(documents.map((d) => d.type_document).filter(Boolean)).size;
  const linkedCount = documents.filter((d) => d.dossier_id || d.organization_id || d.contact_id).length;

  const renderRow = (doc) => (
    <TableRow key={doc.id} className="border-[#E8E6E1] hover:bg-[#F9F8F5]" data-testid={`doc-row-${doc.id}`}>
      <TableCell>
        <div className="flex items-center gap-3">
          {getFileIcon(doc.content_type)}
          <div className="min-w-0">
            <p className="font-medium text-[#1A2421] truncate max-w-[280px]">{doc.titre}</p>
            <p className="text-xs text-[#5E6D66] truncate max-w-[280px]">{doc.original_filename}</p>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <Badge variant="outline" className="text-xs">
          {labelOf(typeDocumentOptions, doc.type_document)}
        </Badge>
      </TableCell>
      <TableCell><StatutBadge statut={doc.statut} /></TableCell>
      <TableCell className="text-xs text-[#5E6D66]">{doc.dossier_id ? getDossierNum(doc.dossier_id) : '—'}</TableCell>
      <TableCell className="text-xs text-[#5E6D66]">{doc.organization_id ? getOrgName(doc.organization_id) : '—'}</TableCell>
      <TableCell className="text-xs text-[#5E6D66]">{doc.contact_id ? getContactName(doc.contact_id) : '—'}</TableCell>
      <TableCell className="text-xs text-[#5E6D66]">{formatFileSize(doc.size)}</TableCell>
      <TableCell className="text-xs text-[#5E6D66]">
        {doc.created_at ? new Date(doc.created_at).toLocaleDateString('fr-FR') : '-'}
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={() => handleView(doc)} data-testid={`view-doc-${doc.id}`} title="Visualiser">
            <Eye size={16} />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => handleDownload(doc)} data-testid={`download-doc-${doc.id}`} title="Télécharger">
            <Download size={16} />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => handleDelete(doc.id)} className="text-[#D47A62]" data-testid={`delete-doc-${doc.id}`} title="Supprimer">
            <Trash size={16} />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );

  const renderHeader = () => (
    <TableHeader>
      <TableRow className="border-[#E8E6E1]">
        <TableHead className="text-xs uppercase tracking-wider text-[#5E6D66]">Document</TableHead>
        <TableHead className="text-xs uppercase tracking-wider text-[#5E6D66]">Type</TableHead>
        <TableHead className="text-xs uppercase tracking-wider text-[#5E6D66]">Statut</TableHead>
        <TableHead className="text-xs uppercase tracking-wider text-[#5E6D66]">Dossier ESS</TableHead>
        <TableHead className="text-xs uppercase tracking-wider text-[#5E6D66]">Organisation</TableHead>
        <TableHead className="text-xs uppercase tracking-wider text-[#5E6D66]">Contact</TableHead>
        <TableHead className="text-xs uppercase tracking-wider text-[#5E6D66]">Taille</TableHead>
        <TableHead className="text-xs uppercase tracking-wider text-[#5E6D66]">Date</TableHead>
        <TableHead className="text-xs uppercase tracking-wider text-[#5E6D66] text-right">Actions</TableHead>
      </TableRow>
    </TableHeader>
  );

  const renderGroupedTab = (groups, emptyHint, testid) => (
    <div className="space-y-4" data-testid={testid}>
      {groups.length === 0 ? (
        <div className="text-center py-12 text-sm text-[#5E6D66]">{emptyHint}</div>
      ) : groups.map((g) => (
        <Card key={g.key} className="border-[#E8E6E1]" data-testid={`ged-group-${g.key}`}>
          <CardContent className="p-0">
            <div className="px-4 py-3 border-b border-[#F0EFEB] flex items-center justify-between bg-[#FAFAF8]">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-[#1A2421]">{g.label}</span>
                <Badge variant="outline" className="text-[10px]">{g.items.length} doc{g.items.length > 1 ? 's' : ''}</Badge>
              </div>
            </div>
            <div className="overflow-x-auto">
              <Table>
                {renderHeader()}
                <TableBody>{g.items.map(renderRow)}</TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="ged-ess-page">
      {/* Hero header — UX transversale v2 */}
      <PageHero
        title="GED ESS"
        subtitle="Gestion Électronique des Documents — Économie Sociale et Solidaire"
        icon={<FileText size={24} weight="duotone" className="text-white" />}
        gradient="from-[#00AEEF] to-[#2D5A4C]"
        accents={['#80C51F', '#FF8E00']}
      />

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="ged-kpis">
        <Card className="border-[#E8E6E1]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[11px] uppercase tracking-wider text-[#5E6D66] font-semibold">Documents</div>
              <FileText size={16} className="text-[#2D5A4C]" />
            </div>
            <div className="text-2xl font-semibold text-[#1A2421]" data-testid="ged-kpi-total">{totalCount}</div>
          </CardContent>
        </Card>
        <Card className="border-[#E8E6E1]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[11px] uppercase tracking-wider text-[#5E6D66] font-semibold">Volume</div>
              <CloudArrowUp size={16} className="text-[#00AEEF]" />
            </div>
            <div className="text-2xl font-semibold text-[#1A2421]">{formatFileSize(totalSize)}</div>
          </CardContent>
        </Card>
        <Card className="border-[#E8E6E1]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[11px] uppercase tracking-wider text-[#5E6D66] font-semibold">Types distincts</div>
              <Tag size={16} className="text-[#FF8E00]" />
            </div>
            <div className="text-2xl font-semibold text-[#1A2421]">{typesCount}</div>
          </CardContent>
        </Card>
        <Card className="border-[#E8E6E1]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[11px] uppercase tracking-wider text-[#5E6D66] font-semibold">Liés à une entité</div>
              <ShieldCheck size={16} className="text-[#80C51F]" />
            </div>
            <div className="text-2xl font-semibold text-[#1A2421]">{linkedCount}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-[#E8E6E1]">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[220px]">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5E6D66]" />
              <Input
                type="search"
                placeholder="Rechercher (titre / fichier)..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 border-[#E8E6E1]"
                data-testid="doc-search"
              />
            </div>

            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[200px] border-[#E8E6E1]" data-testid="ged-filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous types</SelectItem>
                {typeDocumentOptions.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={filterStatut} onValueChange={setFilterStatut}>
              <SelectTrigger className="w-[170px] border-[#E8E6E1]" data-testid="ged-filter-statut"><SelectValue placeholder="Statut" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous statuts</SelectItem>
                {statutOptions.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={filterDossier} onValueChange={setFilterDossier}>
              <SelectTrigger className="w-[190px] border-[#E8E6E1]" data-testid="ged-filter-dossier"><SelectValue placeholder="Dossier ESS" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous dossiers</SelectItem>
                {dossiers.map((d) => <SelectItem key={d.id} value={d.id}>{d.numero_dossier}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={filterOrg} onValueChange={setFilterOrg}>
              <SelectTrigger className="w-[200px] border-[#E8E6E1]" data-testid="ged-filter-org"><SelectValue placeholder="Organisation" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes orgs</SelectItem>
                {organizations.map((o) => <SelectItem key={o.id} value={o.id}>{o.raison_sociale}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={filterContact} onValueChange={setFilterContact}>
              <SelectTrigger className="w-[200px] border-[#E8E6E1]" data-testid="ged-filter-contact"><SelectValue placeholder="Contact" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous contacts</SelectItem>
                {contacts.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {`${c.prenom || ''} ${c.nom || ''}`.trim() || c.email || c.id.slice(0, 8)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tabs for classification */}
      <Tabs value={tab} onValueChange={setTab} className="w-full" data-testid="ged-tabs">
        <TabsList className="bg-white border border-[#E8E6E1] flex flex-wrap gap-1 h-auto p-1">
          <TabsTrigger value="all" data-testid="ged-tab-all"><FileText size={14} className="mr-1.5" /> Tous</TabsTrigger>
          <TabsTrigger value="dossier" data-testid="ged-tab-dossier"><Folder size={14} className="mr-1.5" /> Par dossier</TabsTrigger>
          <TabsTrigger value="org" data-testid="ged-tab-org"><Buildings size={14} className="mr-1.5" /> Par organisation</TabsTrigger>
          <TabsTrigger value="contact" data-testid="ged-tab-contact"><Users size={14} className="mr-1.5" /> Par contact</TabsTrigger>
          <TabsTrigger value="type" data-testid="ged-tab-type"><Tag size={14} className="mr-1.5" /> Par type</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-4">
          <Card className="border-[#E8E6E1]">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="h-7 w-7 animate-spin text-[#2D5A4C]" /></div>
              ) : filteredDocuments.length === 0 ? (
                <div className="text-center py-12" data-testid="ged-empty">
                  <CloudArrowUp size={42} className="mx-auto text-[#E8E6E1] mb-3" />
                  <p className="text-sm text-[#5E6D66]">Aucun document trouvé</p>
                  <Button onClick={() => setDialogOpen(true)} variant="link" className="text-[#2D5A4C] mt-1">
                    Téléverser le premier document
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    {renderHeader()}
                    <TableBody>{filteredDocuments.map(renderRow)}</TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="dossier" className="mt-4">
          {loading ? <div className="flex justify-center py-10"><Loader2 className="h-7 w-7 animate-spin text-[#2D5A4C]" /></div>
            : renderGroupedTab(groupedByDossier, 'Aucun dossier avec des documents pour les filtres actuels.', 'ged-grouped-dossier')}
        </TabsContent>
        <TabsContent value="org" className="mt-4">
          {loading ? <div className="flex justify-center py-10"><Loader2 className="h-7 w-7 animate-spin text-[#2D5A4C]" /></div>
            : renderGroupedTab(groupedByOrg, 'Aucune organisation avec des documents pour les filtres actuels.', 'ged-grouped-org')}
        </TabsContent>
        <TabsContent value="contact" className="mt-4">
          {loading ? <div className="flex justify-center py-10"><Loader2 className="h-7 w-7 animate-spin text-[#2D5A4C]" /></div>
            : renderGroupedTab(groupedByContact, 'Aucun contact avec des documents pour les filtres actuels.', 'ged-grouped-contact')}
        </TabsContent>
        <TabsContent value="type" className="mt-4">
          {loading ? <div className="flex justify-center py-10"><Loader2 className="h-7 w-7 animate-spin text-[#2D5A4C]" /></div>
            : renderGroupedTab(groupedByType, 'Aucun type avec des documents pour les filtres actuels.', 'ged-grouped-type')}
        </TabsContent>
      </Tabs>

      {/* Upload Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetUploadForm(); }}>
        <DialogContent className="max-w-lg" data-testid="ged-upload-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Téléverser un document</DialogTitle>
            <DialogDescription>
              Classez le document en l'associant à un dossier ESS, une organisation et/ou un contact.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4">
            <div className="space-y-2">
              <Label>Fichier *</Label>
              <Input
                ref={fileInputRef}
                type="file"
                onChange={(e) => setUploadData({ ...uploadData, file: e.target.files[0] })}
                className="border-[#E8E6E1]"
                required
                data-testid="file-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Titre</Label>
              <Input
                value={uploadData.titre}
                onChange={(e) => setUploadData({ ...uploadData, titre: e.target.value })}
                placeholder="Laisser vide pour utiliser le nom du fichier"
                data-testid="ged-upload-title"
              />
            </div>
            <div className="space-y-2">
              <Label>Type de document</Label>
              <Select value={uploadData.type_document} onValueChange={(v) => setUploadData({ ...uploadData, type_document: v })}>
                <SelectTrigger data-testid="ged-upload-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {typeDocumentOptions.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>Dossier ESS</Label>
                <Select value={uploadData.dossier_id} onValueChange={(v) => setUploadData({ ...uploadData, dossier_id: v })}>
                  <SelectTrigger data-testid="ged-upload-dossier"><SelectValue placeholder="Aucun" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun</SelectItem>
                    {dossiers.map((d) => <SelectItem key={d.id} value={d.id}>{d.numero_dossier}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Organisation</Label>
                <Select value={uploadData.organization_id} onValueChange={(v) => setUploadData({ ...uploadData, organization_id: v })}>
                  <SelectTrigger data-testid="ged-upload-org"><SelectValue placeholder="Aucune" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucune</SelectItem>
                    {organizations.map((o) => <SelectItem key={o.id} value={o.id}>{o.raison_sociale}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Contact</Label>
                <Select value={uploadData.contact_id} onValueChange={(v) => setUploadData({ ...uploadData, contact_id: v })}>
                  <SelectTrigger data-testid="ged-upload-contact"><SelectValue placeholder="Aucun" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun</SelectItem>
                    {contacts.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {`${c.prenom || ''} ${c.nom || ''}`.trim() || c.email || c.id.slice(0, 8)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetUploadForm(); }} data-testid="ged-upload-cancel">
                Annuler
              </Button>
              <Button type="submit" className="bg-[#2D5A4C] hover:bg-[#1E4136]" disabled={uploading} data-testid="submit-upload-btn">
                {uploading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Téléversement...</>) : 'Téléverser'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
